import {
  Account,
  AccountApi,
  AccountSearchParameters,
  AccountSearchParametersPaging,
  AccountSearchParametersQuery,
  ClosedAccountInfo,
  CompanyInfo,
  PaginatedAccounts,
  PaginatedAccountFromJSON,
  SortOrderParameter,
  MouTemplateType,
  AccountEnrollment,
  AccountStatus,
  CloseInfo,
  BatchClosedAccountInfoRequest,
  EnrollmentChannel,
} from 'eve-account-client';
import { useMemo } from 'react';
import {
  MutationResultPair,
  PaginatedQueryConfig,
  PaginatedQueryResult,
  queryCache,
  QueryConfig,
  QueryResult,
  useMutation,
  usePaginatedQuery,
  useQuery,
} from 'react-query';
import { removeEmptyStrings } from '../utils/util';
import { useConfiguration } from './BaseApi';

const useAccountApi = (): AccountApi => {
  const configuration = useConfiguration();
  const api = useMemo(() => new AccountApi(configuration), [configuration]);
  return api;
};

const getAccountById = (accountId: string, api: AccountApi): Promise<Account> => {
  const request = api.getAccountById({ accountId });
  return request;
};

export const useAccountQuery = (
  accountId?: string,
  queryOptions?: QueryConfig<Account>,
): QueryResult<Account, Error | Response> => {
  const api = useAccountApi();

  return useQuery<Account, Error | Response>(
    [queryOptions?.queryKey ?? 'account', accountId],
    () => getAccountById(accountId as string, api),
    {
      ...queryOptions,
      enabled: Boolean(accountId) && queryOptions?.enabled !== false,
    },
  );
};

export const getAccountEvePlusEligibility = (
  accountId: string,
  api: AccountApi,
  accountLoading: boolean,
  evePlusEnabled: boolean,
): Promise<boolean> | boolean => {
  if (accountLoading || evePlusEnabled) {
    return false;
  }
  const request = api.getEverifyPlusEligibility({ accountId }).then((data) => data?.isEligible || false);
  return request;
};

export const useAccountEvePlusEligibilityQuery = (
  accountId: string,
  accountLoading: boolean,
  evePlusEnabled: boolean,
): QueryResult<boolean, Error | Response> => {
  const api = useAccountApi();

  return useQuery<boolean, Error | Response>(
    ['eligibility', accountId],
    () => getAccountEvePlusEligibility(accountId, api, accountLoading, evePlusEnabled),
    {
      enabled: Boolean(accountId),
    },
  );
};

export const useCreateAccountWithIDMutation = (): MutationResultPair<
  Account,
  Error,
  { accountId: string; accountEnrollment: AccountEnrollment },
  unknown
> => {
  const api = useAccountApi();

  return useMutation<Account, Error, { accountId: string; accountEnrollment: AccountEnrollment }>(
    ({ accountId, accountEnrollment }) => {
      removeEmptyStrings(accountEnrollment);
      return api.createAccountWithId({ accountId, accountEnrollment });
    },
    {
      throwOnError: true,
      useErrorBoundary: false,
    },
  );
};

export const useCreateAccountMutation = (): MutationResultPair<
  Account,
  Error,
  { accountEnrollment: AccountEnrollment },
  unknown
> => {
  const api = useAccountApi();

  return useMutation<Account, Error, { accountEnrollment: AccountEnrollment }>(
    ({ accountEnrollment }) => {
      removeEmptyStrings(accountEnrollment);
      return api.createAccount({ accountEnrollment });
    },
    {
      throwOnError: true,
      useErrorBoundary: false,
    },
  );
};

export const useCreateDependentAccountMutation = (): MutationResultPair<
  Account,
  Error,
  { accountId: string; accountEnrollment: AccountEnrollment },
  unknown
> => {
  const api = useAccountApi();

  return useMutation<Account, Error, { accountId: string; accountEnrollment: AccountEnrollment }>(
    ({ accountId, accountEnrollment }) => {
      removeEmptyStrings(accountEnrollment);
      return api.createDependentAccount({ accountId, accountEnrollment });
    },
    {
      throwOnError: true,
      useErrorBoundary: false,
    },
  );
};

/**
 * @deprecated Use `getAccountById` and take the data from `data.companyInfo`.
 */
const getCompanyInfo = (accountId: string, api: AccountApi): Promise<CompanyInfo> => {
  const request = api.getCompanyInfo({ accountId });
  return request;
};

/**
 * @deprecated Use `useAccountQuery` and take the data from `data.companyInfo`.
 */
export const useCompanyInfoQuery = (accountId?: string): QueryResult<CompanyInfo, Error | Response> => {
  const api = useAccountApi();

  return useQuery<CompanyInfo, Error | Response>(
    ['companyInfo', accountId],
    () => getCompanyInfo(accountId as string, api),
    {
      enabled: Boolean(accountId),
    },
  );
};

const setCompanyInfo = ({
  accountId,
  companyInfo,
  api,
}: {
  accountId: string;
  companyInfo: CompanyInfo;
  api: AccountApi;
}): Promise<CompanyInfo> => {
  removeEmptyStrings(companyInfo);
  return api.setCompanyInfo({ accountId, companyInfo });
};

export const useCompanyInfoMutation = (): MutationResultPair<
  CompanyInfo,
  Error | Response,
  { accountId: string; companyInfo: CompanyInfo },
  unknown
> => {
  const api = useAccountApi();
  return useMutation<CompanyInfo, Error | Response, { accountId: string; companyInfo: CompanyInfo }>(
    ({ accountId, companyInfo }) =>
      setCompanyInfo({
        accountId,
        companyInfo,
        api,
      }),
    {
      onSuccess: (companyInfo, { accountId }) => {
        // Add new company info to the account query cache, if necessary.
        if (queryCache.getQueryData<Account>(['account', accountId])) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          queryCache.setQueryData<Account>(
            ['account', accountId],
            (oldData) => ({ ...oldData, companyInfo } as Account),
          );
        }
        queryCache.setQueryData<CompanyInfo>(['companyInfo', accountId], companyInfo);
      },
      useErrorBoundary: false,
    },
  );
};

const getMouDocument = (accountId: string, api: AccountApi): Promise<Blob> => {
  const request = api.getMouDocument({ accountId });
  return request;
};

const getCombinedMouTosDocument = (accountId: string, api: AccountApi): Promise<Blob> => {
  const request = api.getCombinedMouTosDocument({ accountId });
  return request;
};

const getSignedMouDocument = (accountId: string, api: AccountApi): Promise<Blob> => {
  const request = api.getSignedMouDocument({ accountId });
  return request;
};

const getMouTemplate = (mouTemplateType: MouTemplateType, api: AccountApi): Promise<Blob> => {
  const request = api.getMouTemplate({ mouTemplateType });
  return request;
};

export const useMouDocumentQuery = (
  accountId: string,
  queryOptions?: QueryConfig<Blob>,
): QueryResult<Blob, Error | Response> => {
  const api = useAccountApi();

  return useQuery<Blob, Error | Response>(['mou', accountId], () => getMouDocument(accountId, api), {
    enabled: Boolean(accountId) && queryOptions?.enabled !== false,
  });
};

export const useCombinedMouTosDocumentQuery = (
  accountId: string,
  queryOptions?: QueryConfig<Blob>,
): QueryResult<Blob, Error | Response> => {
  const api = useAccountApi();

  return useQuery<Blob, Error | Response>(['mou-tos', accountId], () => getCombinedMouTosDocument(accountId, api), {
    enabled: Boolean(accountId) && queryOptions?.enabled !== false,
  });
};

export const useSignedMouDocumentQuery = (
  accountId: string,
  queryOptions?: QueryConfig<Blob>,
): QueryResult<Blob, Error | Response> => {
  const api = useAccountApi();

  return useQuery<Blob, Error | Response>(['mou', accountId], () => getSignedMouDocument(accountId, api), {
    enabled: Boolean(accountId) && queryOptions?.enabled !== false,
  });
};

export const useMouTemplateQuery = (
  mouTemplateType: MouTemplateType,
  queryOptions?: QueryConfig<Blob>,
): QueryResult<Blob, Error | Response> => {
  const api = useAccountApi();

  return useQuery<Blob, Error | Response>(['mou', mouTemplateType], () => getMouTemplate(mouTemplateType, api), {
    enabled: Boolean(mouTemplateType) && queryOptions?.enabled !== false,
  });
};

const getTosDocument = (accountId: string, api: AccountApi): Promise<Blob> => {
  const request = api.getTosDocument({ accountId });
  return request;
};

export const useTosDocumentQuery = (
  accountId: string,
  queryOptions?: QueryConfig<Blob>,
): QueryResult<Blob, Error | Response> => {
  const api = useAccountApi();

  return useQuery<Blob, Error | Response>(['myI9', accountId], () => getTosDocument(accountId, api), {
    enabled: Boolean(accountId) && queryOptions?.enabled !== false,
  });
};

const searchAccounts = async (searchParams: AccountSearchParameters, api: AccountApi) => {
  const original = await api.searchAccountsNew({
    accountSearchParameters: searchParams,
  });

  // Re-transform to get actual deserialized paginated accounts.
  // See https://github.com/OpenAPITools/openapi-generator/issues/1563 for more info.
  return {
    ...original,
    paginated: PaginatedAccountFromJSON(original.paginated),
  } as PaginatedAccounts;
};

export const useSearchAccountQuery = (
  query?: AccountSearchParametersQuery,
  sort?: SortOrderParameter[],
  paging: AccountSearchParametersPaging = { page: 0, size: 1 },
  queryOptions?: PaginatedQueryConfig<PaginatedAccounts, Error | Response>,
): PaginatedQueryResult<PaginatedAccounts, Error | Response> => {
  const api = useAccountApi();

  return usePaginatedQuery(
    ['searchAccounts', { paging, sort, query }],
    () => searchAccounts({ paging, query, sort }, api),
    {
      ...queryOptions,
    },
  );
};

const closeBatchAccounts = ({
  accountId,
  batchClosedAccountInfoRequest,
  api,
}: {
  accountId: string;
  batchClosedAccountInfoRequest: Array<BatchClosedAccountInfoRequest>;
  api: AccountApi;
}): Promise<Array<BatchClosedAccountInfoRequest>> => {
  return api.postBatchClosedAccountInfoRequest({ accountId, batchClosedAccountInfoRequest });
};

const getClosedAccountInfo = (accountId: string, api: AccountApi): Promise<ClosedAccountInfo> => {
  const request = api.getClosedAccountInfo({ accountId });
  return request;
};

export const useClosedAccountInfoQuery = (accountId?: string): QueryResult<ClosedAccountInfo, Error | Response> => {
  const api = useAccountApi();

  return useQuery<ClosedAccountInfo, Error | Response>(
    ['accountId', accountId],
    () => getClosedAccountInfo(accountId as string, api),
    {
      enabled: Boolean(accountId),
    },
  );
};

export const useCloseBatchAccountMutation = (
  onError: (error: Error | Response) => void,
): MutationResultPair<
  Array<BatchClosedAccountInfoRequest>,
  Error | Response,
  { accountId: string; batchClosedAccountInfoRequest: Array<BatchClosedAccountInfoRequest> },
  unknown
> => {
  const api = useAccountApi();

  return useMutation<
    Array<BatchClosedAccountInfoRequest>,
    Error | Response,
    { accountId: string; batchClosedAccountInfoRequest: Array<BatchClosedAccountInfoRequest> }
  >(
    ({ accountId, batchClosedAccountInfoRequest }) =>
      closeBatchAccounts({
        accountId,
        batchClosedAccountInfoRequest,
        api,
      }),
    {
      onSuccess: (batchClosedAccountInfoRequest, { accountId }) => {
        queryCache.setQueryData<Array<BatchClosedAccountInfoRequest>>(
          ['batchClosedAccountInfoRequest', accountId],
          batchClosedAccountInfoRequest,
        );
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        queryCache.invalidateQueries(['account', accountId]);
      },
      onError: (error) => {
        onError(error);
      },
      throwOnError: true,
      useErrorBoundary: false,
    },
  );
};

const updateAccountStatus = ({
  accountId,
  status,
  closingInfo,
  api,
}: {
  accountId: string;
  status: AccountStatus;
  closingInfo?: CloseInfo;
  api: AccountApi;
}): Promise<ClosedAccountInfo> => {
  return api.updateStatus({
    accountId,
    updateStatusRequest: { status, closingInfo },
  });
};

export const useAccountStatusUpdateMutation = (
  onError: (error: Error | Response) => void,
): MutationResultPair<
  ClosedAccountInfo,
  Error | Response,
  { accountId: string; status: AccountStatus; closingInfo?: CloseInfo },
  unknown
> => {
  const api = useAccountApi();

  return useMutation<
    ClosedAccountInfo,
    Error | Response,
    { accountId: string; status: AccountStatus; closingInfo?: CloseInfo },
    unknown
  >(
    ({ accountId, status, closingInfo }) =>
      updateAccountStatus({
        accountId,
        status,
        closingInfo,
        api,
      }),
    {
      onSuccess: (closedAccountInfo, { accountId }) => {
        queryCache.setQueryData<ClosedAccountInfo>(['closedAccountInfo', accountId], closedAccountInfo);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        queryCache.invalidateQueries(['account', accountId]);
      },
      onError: (error) => {
        onError(error);
      },
      throwOnError: true,
      useErrorBoundary: false,
    },
  );
};

export const useSignMouDocumentMutation = (): MutationResultPair<
  void,
  Error,
  { accountId: string; file: Blob; marketingChannel: EnrollmentChannel },
  unknown
> => {
  const api = useAccountApi();

  return useMutation<void, Error, { accountId: string; file: Blob; marketingChannel: EnrollmentChannel }>(
    ({ accountId, file, marketingChannel }) => {
      const marketingQuestion = marketingChannel?.secondaryValue
        ? marketingChannel.secondaryValue
        : marketingChannel.primaryValue;
      return api.postSignedMouDocument({ accountId, file, marketingChannel: marketingQuestion });
    },
    {
      throwOnError: true,
      useErrorBoundary: false,
    },
  );
};

export const useResendMouEmailMutation = (
  onError: (error: Error | Response) => void,
): MutationResultPair<void, Error, { accountId: string }, unknown> => {
  const api = useAccountApi();

  return useMutation<void, Error, { accountId: string }>(
    ({ accountId }) => {
      return api.sendMouSignatureEmail({ accountId });
    },
    {
      onError: (error) => {
        onError(error);
      },
      throwOnError: true,
      useErrorBoundary: false,
    },
  );
};
